<script setup>
import { ref, computed, inject } from "vue";
import axios from "axios";

const env = inject("env", {
    templateId: null,
    template: "{}",
    referenceTemplates: "[]",
    categories: "{}",
    snippetsByCat: "{}",
    sectorOptions: "{}",
    sortCategoryLabels: "{}",
    sortCategoryOptions: "{}",
});

const templateId = env.templateId;

const core = ref({
    title: "",
    prompt_base: "",
    reference_template_id: "",
    is_referenceable: false,
    is_fallback: false,
    subscription_id: null,
    reference_template_title: null,
});

const coreErrors = ref({});
const coreSaving = ref(false);

const referenceTemplates = ref([]);
const categories = ref({});
const snippetsByCat = ref({});
const sectorOptions = ref({});
const sortCategoryLabels = ref({});
const sortCategoryOptions = ref({});
const sectorFilter = ref("");
const overrideReferencePrompt = ref(false);

try {
    if (env.template) {
        const t =
            typeof env.template === "string"
                ? JSON.parse(env.template)
                : env.template;
        t.is_referenceable = !!t.is_referenceable;
        t.is_fallback = !!t.is_fallback;
        core.value = {
            ...core.value,
            ...t,
        };
    }
    if (env.referenceTemplates) {
        referenceTemplates.value =
            typeof env.referenceTemplates === "string"
                ? JSON.parse(env.referenceTemplates)
                : env.referenceTemplates;
    }
    if (env.categories) {
        categories.value =
            typeof env.categories === "string"
                ? JSON.parse(env.categories)
                : env.categories;
    }
    if (env.snippetsByCat) {
        snippetsByCat.value =
            typeof env.snippetsByCat === "string"
                ? JSON.parse(env.snippetsByCat)
                : env.snippetsByCat;
    }
    if (env.sectorOptions) {
        sectorOptions.value =
            typeof env.sectorOptions === "string"
                ? JSON.parse(env.sectorOptions)
                : env.sectorOptions;
    }
    if (env.sortCategoryLabels) {
        sortCategoryLabels.value =
            typeof env.sortCategoryLabels === "string"
                ? JSON.parse(env.sortCategoryLabels)
                : env.sortCategoryLabels;
    }
    if (env.sortCategoryOptions) {
        sortCategoryOptions.value =
            typeof env.sortCategoryOptions === "string"
                ? JSON.parse(env.sortCategoryOptions)
                : env.sortCategoryOptions;
    }
} catch (e) {
    console.error("Error parsing env data in PromptOptionTemplateEdit", e);
}

// Build a more convenient structure for snippets
const groupedSnippets = computed(() => {
    const result = {};
    Object.keys(categories.value).forEach((catKey) => {
        // sector filter only applies for global templates (no subscription)
        const activeFilter = !core.value.subscription_id
            ? sectorFilter.value
            : "";
        const source = snippetsByCat.value[catKey] || [];

        const filtered = source.filter((sn) => {
            if (!activeFilter) {
                return true;
            }
            const belongs = !!sn.pivot;
            return belongs || sn.sector === activeFilter;
        });

        const mapped = filtered.map((sn) => {
            const belongs = !!sn.pivot;
            return {
                id: sn.id,
                title: sn.title,
                sector: sn.sector,
                ui_marker: sn.ui_marker,
                configCategoryLabel: sn.config_category_label || null,
                subscriptionName: sn.subscription_name || null,
                selected: belongs,
                sort_order:
                    belongs && sn.pivot.sort_order !== null
                        ? sn.pivot.sort_order
                        : "",
                sort_category:
                    belongs && sn.pivot.sort_category !== null
                        ? sn.pivot.sort_category
                        : "",
            };
        });

        // sort by sort_order (empty/null last), then by title
        mapped.sort((a, b) => {
            const aHasOrder = a.sort_order !== "" && a.sort_order !== null;
            const bHasOrder = b.sort_order !== "" && b.sort_order !== null;

            if (aHasOrder && bHasOrder) {
                const aNum = Number(a.sort_order);
                const bNum = Number(b.sort_order);
                if (aNum !== bNum) return aNum - bNum;
            } else if (aHasOrder && !bHasOrder) {
                return -1;
            } else if (!aHasOrder && bHasOrder) {
                return 1;
            }

            return (a.title || "").localeCompare(b.title || "");
        });

        result[catKey] = mapped;
    });
    return result;
});

const saveStatus = ref("bereit");
const saveStatusClass = ref("text-muted");
let saveTimeout = null;

/** id -> snippet row from groupedSnippets (latest reference per id) */
const pendingSnippetsById = new Map();
let batchSaveTimer = null;

function setSaveStatus(text, cls = "") {
    saveStatus.value = text;
    saveStatusClass.value = cls;
    if (saveTimeout) {
        clearTimeout(saveTimeout);
        saveTimeout = null;
    }
    if (text === "gespeichert") {
        saveTimeout = setTimeout(() => {
            saveStatus.value = "bereit";
            saveStatusClass.value = "text-muted";
        }, 2000);
    }
}

async function saveCore(event) {
    event.preventDefault();
    coreSaving.value = true;
    coreErrors.value = {};

    try {
        const payload = {
            title: core.value.title || "",
            prompt_base: core.value.prompt_base || "",
            reference_template_id: core.value.reference_template_id || "",
        };

        if (!core.value.subscription_id) {
            // only allow toggling is_referenceable / is_fallback for global templates
            payload.is_referenceable = core.value.is_referenceable ? 1 : 0;
            payload.is_fallback = core.value.is_fallback ? 1 : 0;
        }

        await axios.patch(`/ajax/promptOptionTemplates/${templateId}`, payload);
    } catch (error) {
        if (
            error.response &&
            error.response.data &&
            error.response.data.errors
        ) {
            coreErrors.value = error.response.data.errors;
        }
        console.error(error);
    } finally {
        coreSaving.value = false;
    }
}

async function saveSnippets(snippets) {
    if (!snippets.length) {
        return;
    }
    setSaveStatus("speichert...", "text-warning");
    const body = { snippets: {} };
    for (const snippet of snippets) {
        body.snippets[snippet.id] = {
            selected: snippet.selected ? 1 : 0,
            sort_order: snippet.sort_order !== "" ? snippet.sort_order : null,
            sort_category:
                snippet.sort_category !== "" ? snippet.sort_category : null,
        };
    }
    try {
        await axios.patch(
            `/ajax/promptOptionTemplates/${templateId}/snippets`,
            body,
        );
        setSaveStatus("gespeichert", "text-success");
    } catch (error) {
        console.error(error);
        setSaveStatus("Fehler beim Speichern", "text-danger");
    }
}

function onSnippetChange(snippet) {
    pendingSnippetsById.set(snippet.id, snippet);
    setSaveStatus("Änderung erkannt, speichere...", "text-muted");
    if (batchSaveTimer) {
        clearTimeout(batchSaveTimer);
    }
    batchSaveTimer = setTimeout(() => {
        batchSaveTimer = null;
        const snippets = [...pendingSnippetsById.values()];
        pendingSnippetsById.clear();
        if (snippets.length) {
            saveSnippets(snippets);
        }
    }, 1000);
}

function selectAllInCategory(catKey) {
    const list = groupedSnippets.value[catKey];
    if (!list || !list.length) {
        return;
    }
    for (const snippet of list) {
        if (!snippet.selected) {
            snippet.selected = true;
            onSnippetChange(snippet);
        }
    }
}

function deselectAllInCategory(catKey) {
    const list = groupedSnippets.value[catKey];
    if (!list || !list.length) {
        return;
    }
    for (const snippet of list) {
        if (snippet.selected) {
            snippet.selected = false;
            onSnippetChange(snippet);
        }
    }
}

function undoOverrideReferencePrompt() {
    overrideReferencePrompt.value = false;
    core.value.prompt_base = null;
}
</script>

<template>
    <div>
        <!-- Core form -->
        <form class="mb-4" @submit.prevent="saveCore">
            <div class="mb-3">
                <label for="title" class="form-label">Titel der Vorlage</label>
                <input type="text" id="title" name="title" class="form-control" v-model="core.title" required />
                <div class="text-danger small" v-if="coreErrors.title">
                    {{ coreErrors.title[0] }}
                </div>
            </div>

            <div v-if="!core.subscription_id" class="mb-3">
                <label for="is_referenceable" class="form-check-label ml-4"
                    :class="core.reference_template_id ? 'text-muted' : ''">
                    <input type="checkbox" id="is_referenceable" name="is_referenceable" value="1"
                        class="form-check-input" :disabled="!!core.reference_template_id"
                        v-model="core.is_referenceable" />
                    Referenzierbar
                </label>
            </div>

            <div v-if="!core.subscription_id" class="mb-3">
                <label for="is_fallback" class="form-check-label ml-4">
                    <input type="checkbox" id="is_fallback" name="is_fallback" value="1"
                        class="form-check-input" v-model="core.is_fallback" />
                    Fallback für Subscriptions ohne eigene Vorlagen
                </label>
            </div>

            <div v-if="!core.is_referenceable && core.subscription_id" class="mb-3">
                <label for="reference_template_id" class="form-label">Referenzierte Vorlage</label>
                <select id="reference_template_id" name="reference_template_id" class="form-control"
                    v-model="core.reference_template_id">
                    <option value="">Keine</option>
                    <option v-for="(title, id) in referenceTemplates" :key="id" :value="id">
                        {{ title }}
                    </option>
                </select>
                <div class="text-danger small" v-if="coreErrors.reference_template_id">
                    {{ coreErrors.reference_template_id[0] }}
                </div>
            </div>

            <div class="mb-3">
                <label for="prompt_base" class="form-label">Basis-Prompt</label>
                <div v-if="
                    core.reference_template_id &&
                    !core.prompt_base &&
                    core.reference_template_title &&
                    !overrideReferencePrompt
                ">
                    <div class="d-flex justify-content-between align-items-center mb-2">

                        <span>Referenziert aus "<strong>{{
                            core.reference_template_title
                                }}</strong>":</span>
                        <button class="btn btn-sm btn-outline-primary"
                            @click="overrideReferencePrompt = true">Überschreiben</button>
                    </div>
                    <pre class="card p-2 mb-0"
                        style="max-height: 200px; overflow: auto;">{{ core.reference_template_prompt_base }}</pre>
                </div>
                <div v-else>
                    <div v-if="overrideReferencePrompt" class="d-flex justify-content-between align-items-center mb-2">
                        <span>Überschreiben mit:</span>
                        <button class="btn btn-sm btn-outline-primary"
                            @click="undoOverrideReferencePrompt">Abbrechen</button>
                    </div>
                    <textarea id="prompt_base" name="prompt_base" class="form-control" rows="3"
                        v-model="core.prompt_base"></textarea>
                    <div class="text-danger small" v-if="coreErrors.prompt_base">
                        {{ coreErrors.prompt_base[0] }}
                    </div>
                </div>
            </div>

            <div class="d-flex gap-2 mt-3">
                <button type="submit" class="btn btn-primary" :disabled="coreSaving">
                    {{ coreSaving ? "Speichert..." : "Speichern" }}
                </button>
            </div>
        </form>
        <hr />
        <h4 class="mb-3">Optionen bearbeiten</h4>

        <!-- Sector filter (only for global templates) -->
        <div v-if="!core.subscription_id" class="mb-3">
            <label class="form-label">
                Nach Branche filtern:
                <select class="form-control" v-model="sectorFilter">
                    <option value="">alle Branchen</option>
                    <option v-for="(label, key) in sectorOptions" :key="key" :value="key">
                        {{ label }}
                    </option>
                </select>
            </label>
        </div>

        <!-- Live snippets -->
        <div class="mb-2 small text-muted">
            Status:
            <span class="font-weight-bold" :class="saveStatusClass">{{
                saveStatus
                }}</span>
        </div>
        <div v-for="(catLabel, catKey) in categories" :key="catKey" class="mt-4">
            <div class="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-2">
                <h5 class="mb-0">{{ catLabel }}</h5>
                <div v-if="
                    groupedSnippets[catKey] &&
                    groupedSnippets[catKey].length
                " class="d-flex flex-wrap align-items-center gap-2">
                    <span class="small text-muted mb-0">alle</span>
                    <button type="button" class="btn btn-sm btn-outline-secondary ml-2" title="Alle auswählen"
                        aria-label="Alle auswählen" @click="selectAllInCategory(catKey)">
                        <i class="bi bi-check2-square" aria-hidden="true"></i>
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary ml-2" title="Alle abwählen"
                        aria-label="Alle abwählen" @click="deselectAllInCategory(catKey)">
                        <i class="bi bi-square" aria-hidden="true"></i>
                    </button>
                </div>
            </div>
            <ul class="list-group mb-3">
                <li v-for="snippet in groupedSnippets[catKey]" :key="snippet.id"
                    class="list-group-item d-flex justify-content-between align-items-center"
                    :style="snippet.ui_marker === 'from_reference' ? 'background-color: #eeeeff;' : snippet.ui_marker === 'assigned_floating' ? 'background-color: #ffeeee;' : ''">
                    <div class="d-flex align-items-center gap-2">
                        <label class="form-check-label mb-0 ml-4">
                            <input type="checkbox" class="form-check-input" v-model="snippet.selected"
                                @change="onSnippetChange(snippet)" />
                            {{ snippet.title }}
                            <span v-if="snippet.subscriptionName" class="text-muted">
                                - {{ snippet.subscriptionName }}
                            </span>
                            <span v-if="
                                snippet.sector &&
                                sectorOptions[snippet.sector]
                            " class="text-muted">
                                - {{ sectorOptions[snippet.sector] }}
                            </span>
                            <span v-if="snippet.configCategoryLabel" class="text-muted">
                                ({{ snippet.configCategoryLabel }})
                            </span>
                        </label>
                    </div>

                    <div v-if="snippet.selected" class="d-flex align-items-center gap-2">
                        <label class="form-label mb-0 small text-muted mr-2">Sortierung</label>
                        <input type="number" class="form-control form-control-sm" style="width: 90px"
                            v-model="snippet.sort_order" min="0" step="1" @change="onSnippetChange(snippet)" />

                        <select v-if="sortCategoryOptions[catKey] && sortCategoryOptions[catKey].length"
                            class="form-control form-control-sm ml-3" v-model="snippet.sort_category"
                            @change="onSnippetChange(snippet)">
                            <option value="">frei</option>
                            <option v-for="(option, key) in sortCategoryOptions[catKey]" :key="option" :value="option">
                                {{ sortCategoryLabels[option] }}
                            </option>
                        </select>
                    </div>
                </li>
                <li v-if="
                    !groupedSnippets[catKey] ||
                    !groupedSnippets[catKey].length
                " class="list-group-item text-muted">
                    Keine Optionen vorhanden.
                </li>
            </ul>
        </div>
    </div>
</template>
